/****************************************************************************
 * Copyright 2021 EPAM Systems
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *    http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 ***************************************************************************/

import type { PolymerBond } from 'domain/entities/PolymerBond';
import type { RenderersManager } from 'application/render/renderers/RenderersManager';
import type { Operation } from 'domain/entities/Operation';
import type { BaseMonomer } from 'domain/entities/BaseMonomer';
import type { HydrogenBond } from 'domain/entities/HydrogenBond';
import type { LayoutMode } from 'application/editor/modes/types';

export class PolymerBondAddOperation implements Operation {
  public polymerBond;
  public priority = 1;
  constructor(
    private readonly addPolymerBondChangeModel: (
      polymerBond?: PolymerBond | HydrogenBond,
    ) => PolymerBond | HydrogenBond,
    private readonly deletePolymerBondChangeModel: (polymerBond) => void,
  ) {
    this.polymerBond = this.addPolymerBondChangeModel();
  }

  public execute(renderersManager: RenderersManager) {
    this.polymerBond = this.addPolymerBondChangeModel(this.polymerBond);
    renderersManager.addPolymerBond(this.polymerBond);
  }

  public invert(renderersManager: RenderersManager) {
    this.deletePolymerBondChangeModel(this.polymerBond);
    renderersManager.deletePolymerBond(this.polymerBond);
  }
}

export class PolymerBondDeleteOperation implements Operation {
  public priority = -1;
  constructor(
    public polymerBond: PolymerBond | HydrogenBond,
    private readonly deletePolymerBondChangeModel: () => void,
    private readonly finishPolymerBondCreationModelChange: (
      polymerBond?: PolymerBond | HydrogenBond,
    ) => PolymerBond | HydrogenBond,
  ) {
    this.deletePolymerBondChangeModel();
  }

  public execute(renderersManager: RenderersManager) {
    this.deletePolymerBondChangeModel();
    renderersManager.deletePolymerBond(this.polymerBond);
  }

  public invert(renderersManager: RenderersManager) {
    this.polymerBond = this.finishPolymerBondCreationModelChange(
      this.polymerBond,
    );
    renderersManager.addPolymerBond(this.polymerBond);
  }
}

export class PolymerBondMoveOperation implements Operation {
  constructor(public polymerBond: PolymerBond) {}

  public execute(renderersManager: RenderersManager) {
    renderersManager.movePolymerBond(this.polymerBond);
  }

  public invert() {
    // intentional no-op: move state is transient and has no undo state
  }
}

export class PolymerBondShowInfoOperation implements Operation {
  constructor(public polymerBond: PolymerBond) {}

  public execute(renderersManager: RenderersManager) {
    renderersManager.showPolymerBondInformation(this.polymerBond);
  }

  public invert() {
    // intentional no-op: show info state is transient and has no undo state
  }
}

export class PolymerBondCancelCreationOperation implements Operation {
  constructor(
    public polymerBond: PolymerBond,
    private readonly secondMonomer?: BaseMonomer,
  ) {}

  public execute(renderersManager: RenderersManager) {
    renderersManager.cancelPolymerBondCreation(
      this.polymerBond,
      this.secondMonomer,
    );
  }

  public invert() {
    // intentional no-op: cancel creation is a one-way operation with no undo state
  }
}

export class PolymerBondFinishCreationOperation implements Operation {
  public polymerBond;
  public priority = 1;
  constructor(
    private readonly finishPolymerBondCreationModelChange: (
      polymerBond?: PolymerBond,
    ) => PolymerBond,
    private readonly deletePolymerBondCreationModelChange: (
      polymerBond,
    ) => void,
  ) {
    this.polymerBond = this.finishPolymerBondCreationModelChange();
  }

  public execute(renderersManager: RenderersManager) {
    this.polymerBond = this.finishPolymerBondCreationModelChange(
      this.polymerBond,
    );
    renderersManager.finishPolymerBondCreation(this.polymerBond);
  }

  public invert(renderersManager: RenderersManager) {
    this.deletePolymerBondCreationModelChange(this.polymerBond);
    renderersManager.deletePolymerBond(this.polymerBond);
  }
}

export class SelectLayoutModeOperation implements Operation {
  private readonly onExecute;
  private readonly onInvert;

  constructor(
    public _onExecute: () => void,
    public _onInvert: () => void,
    public mode: LayoutMode,
    public prevMode,
  ) {
    this.onExecute = _onExecute;
    this.onInvert = _onInvert;
  }

  public execute(): void {
    this.onExecute();
  }

  public invert(): void {
    this.onInvert();
  }
}

export class ReconnectPolymerBondOperation implements Operation {
  public polymerBond;
  constructor(
    private readonly reconnectPolymerBondModelChange: () => PolymerBond,
    private readonly revertReconnectPolymerBondModelChange: () => PolymerBond,
  ) {}

  public execute(renderersManager: RenderersManager) {
    this.polymerBond = this.reconnectPolymerBondModelChange();
    renderersManager.redrawDrawingEntity(this.polymerBond, false, true);
  }

  public invert(renderersManager: RenderersManager) {
    this.polymerBond = this.revertReconnectPolymerBondModelChange();
    renderersManager.redrawDrawingEntity(this.polymerBond, false, true);
  }
}
